import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "console-panel dash-calendar" }
const _hoisted_2 = { class: "console-panel-head" }
const _hoisted_3 = { class: "cal-nav" }
const _hoisted_4 = { class: "console-panel-title cal-label" }
const _hoisted_5 = { class: "cal-split" }
const _hoisted_6 = { class: "cal-board" }
const _hoisted_7 = { class: "cal-week" }
const _hoisted_8 = { class: "cal-days" }
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "cal-num" }
const _hoisted_11 = {
  key: 0,
  class: "cal-name"
}
const _hoisted_12 = {
  key: 1,
  class: "cal-dot",
  "aria-label": "메모 있음"
}
const _hoisted_13 = { class: "cal-memo" }
const _hoisted_14 = { class: "cal-memo-date" }
const _hoisted_15 = { class: "cal-memo-hint" }
const _hoisted_16 = { class: "cal-memo-foot" }
const _hoisted_17 = { class: "cal-memo-state" }
const _hoisted_18 = ["disabled"]

import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { dashMemoApi, holidayManageApi } from '@/services/api';

interface HolidayItem {
  date: string;
  name: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DashCalendar',
  setup(__props) {

const router = useRouter();
const now = new Date();
const weekNames = ['일', '월', '화', '수', '목', '금', '토'];
const weekFull = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];

const pad = (value: number) => String(value).padStart(2, '0');

const dateKeyOf = (year: number, month: number, day: number) =>
  `${year}-${pad(month)}-${pad(day)}`;

const viewYear = ref(now.getFullYear());
const viewMonth = ref(now.getMonth() + 1);
const monthHolidays = ref<HolidayItem[]>([]);
const memos = ref<Record<string, string>>({});
const selectedDate = ref(dateKeyOf(now.getFullYear(), now.getMonth() + 1, now.getDate()));
const draft = ref('');
const savedBody = ref('');
const saving = ref(false);
const memoState = ref('');

const calendarCells = computed(() => {
  const firstDow = new Date(viewYear.value, viewMonth.value - 1, 1).getDay();
  const lastDate = new Date(viewYear.value, viewMonth.value, 0).getDate();
  const cells: Array<number | null> = [];
  for (let i = 0; i < firstDow; i += 1) cells.push(null);
  for (let day = 1; day <= lastDate; day += 1) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
});

const holidayMap = computed(() => {
  const map = new Map<string, HolidayItem>();
  monthHolidays.value.forEach((item) => map.set(item.date, item));
  return map;
});

const dateKey = (day: number) => dateKeyOf(viewYear.value, viewMonth.value, day);

const holidayOn = (day: number) => holidayMap.value.get(dateKey(day)) || null;
const holidayName = (day: number) => holidayOn(day)?.name || '';
const hasMemo = (day: number) => !!memos.value[dateKey(day)];

const isToday = (day: number) =>
  viewYear.value === now.getFullYear()
  && viewMonth.value === now.getMonth() + 1
  && day === now.getDate();

const isPicked = (day: number) => dateKey(day) === selectedDate.value;

const selectedParts = computed(() => {
  const [year, month, day] = selectedDate.value.split('-').map((part) => Number(part));
  const date = new Date(year, month - 1, day);
  return { year, month, day, weekday: weekFull[date.getDay()] };
});

const selectedLabel = computed(() => {
  const { month, day, weekday } = selectedParts.value;
  return `${month}월 ${day}일 ${weekday}`;
});

const selectedHint = computed(() => {
  const today = dateKeyOf(now.getFullYear(), now.getMonth() + 1, now.getDate());
  if (selectedDate.value === today) return '오늘';
  return holidayMap.value.get(selectedDate.value)?.name || '';
});

const applyDraft = (date: string) => {
  const body = memos.value[date] || '';
  draft.value = body;
  savedBody.value = body;
  memoState.value = '';
};

const loadHolidays = async () => {
  try {
    const res = await holidayManageApi.listByMonth(viewYear.value, viewMonth.value);
    monthHolidays.value = res.data?.data?.items || [];
  } catch (error) {
    monthHolidays.value = [];
  }
};

const loadMemos = async () => {
  try {
    const res = await dashMemoApi.listMonth(viewYear.value, viewMonth.value);
    const next: Record<string, string> = {};
    (res.data?.data?.items || []).forEach((item: { date: string; body: string }) => {
      if (item?.date && item.body) next[item.date] = item.body;
    });
    memos.value = next;
  } catch (error) {
    memos.value = {};
  }
  if (selectedDate.value.startsWith(`${viewYear.value}-${pad(viewMonth.value)}`)) {
    applyDraft(selectedDate.value);
  }
};

const loadMonth = () => {
  loadHolidays();
  loadMemos();
};

const shiftMonth = async (delta: number) => {
  await saveIfDirty();
  const next = new Date(viewYear.value, viewMonth.value - 1 + delta, 1);
  viewYear.value = next.getFullYear();
  viewMonth.value = next.getMonth() + 1;
  const lastDate = new Date(viewYear.value, viewMonth.value, 0).getDate();
  const day = Math.min(selectedParts.value.day, lastDate);
  const today = dateKeyOf(now.getFullYear(), now.getMonth() + 1, now.getDate());
  const sameMonth = viewYear.value === now.getFullYear() && viewMonth.value === now.getMonth() + 1;
  selectedDate.value = sameMonth ? today : dateKeyOf(viewYear.value, viewMonth.value, day);
  draft.value = '';
  savedBody.value = '';
  memoState.value = '';
  loadMonth();
};

const pickDay = async (day: number) => {
  const next = dateKey(day);
  if (next === selectedDate.value) return;
  await saveIfDirty();
  selectedDate.value = next;
  applyDraft(next);
};

let savingTask: Promise<void> | null = null;

const saveMemo = () => {
  if (savingTask) return savingTask;
  const date = selectedDate.value;
  const body = draft.value.trim();
  if (body === savedBody.value) {
    memoState.value = body ? '저장되어 있습니다' : '';
    return Promise.resolve();
  }
  saving.value = true;
  memoState.value = '저장 중';
  savingTask = (async () => {
    try {
      await dashMemoApi.save(date, body);
      const next = { ...memos.value };
      if (body) next[date] = body;
      else delete next[date];
      memos.value = next;
      if (selectedDate.value === date) {
        draft.value = body;
        savedBody.value = body;
        memoState.value = '저장했습니다';
      }
    } catch (error) {
      if (selectedDate.value === date) memoState.value = '저장하지 못했습니다';
    } finally {
      saving.value = false;
      savingTask = null;
    }
  })();
  return savingTask;
};

const saveIfDirty = async () => {
  if (draft.value.trim() === savedBody.value) return;
  await saveMemo();
};

const goHoliday = () => router.push('/holiday-manage');

onMounted(loadMonth);

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("button", {
          type: "button",
          class: "cal-chevron",
          "aria-label": "이전 달",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (shiftMonth(-1)))
        }, [
          _createVNode(_component_v_icon, { size: "18" }, {
            default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
              _createTextVNode("mdi-chevron-left", -1)
            ]))]),
            _: 1
          })
        ]),
        _createElementVNode("h3", _hoisted_4, _toDisplayString(viewYear.value) + "년 " + _toDisplayString(viewMonth.value) + "월", 1),
        _createElementVNode("button", {
          type: "button",
          class: "cal-chevron",
          "aria-label": "다음 달",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (shiftMonth(1)))
        }, [
          _createVNode(_component_v_icon, { size: "18" }, {
            default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
              _createTextVNode("mdi-chevron-right", -1)
            ]))]),
            _: 1
          })
        ])
      ]),
      _createElementVNode("button", {
        type: "button",
        class: "console-text-btn",
        "aria-label": "공휴일관리로 이동",
        onClick: goHoliday
      }, "이동")
    ]),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("div", _hoisted_7, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(weekNames, (name) => {
            return _createElementVNode("span", { key: name }, _toDisplayString(name), 1)
          }), 64))
        ]),
        _createElementVNode("div", _hoisted_8, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(calendarCells.value, (cell, index) => {
            return (_openBlock(), _createElementBlock("span", {
              key: index,
              class: "cal-cell"
            }, [
              cell
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    type: "button",
                    class: _normalizeClass(["cal-day", {
                'is-today': isToday(cell),
                'is-picked': isPicked(cell),
                'is-holiday': !!holidayOn(cell),
                'is-sun': index % 7 === 0,
                'is-sat': index % 7 === 6,
              }]),
                    onClick: ($event: any) => (pickDay(cell))
                  }, [
                    _createElementVNode("span", _hoisted_10, _toDisplayString(cell), 1),
                    (holidayName(cell))
                      ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(holidayName(cell)), 1))
                      : _createCommentVNode("", true),
                    (hasMemo(cell))
                      ? (_openBlock(), _createElementBlock("i", _hoisted_12))
                      : _createCommentVNode("", true)
                  ], 10, _hoisted_9))
                : _createCommentVNode("", true)
            ]))
          }), 128))
        ])
      ]),
      _createElementVNode("aside", _hoisted_13, [
        _createElementVNode("p", _hoisted_14, _toDisplayString(selectedLabel.value), 1),
        _createElementVNode("p", _hoisted_15, _toDisplayString(selectedHint.value), 1),
        _withDirectives(_createElementVNode("textarea", {
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((draft).value = $event)),
          maxlength: "2000",
          placeholder: "이 날에 남겨 둘 말을 적어 주세요"
        }, null, 512), [
          [_vModelText, draft.value]
        ]),
        _createElementVNode("div", _hoisted_16, [
          _createElementVNode("span", _hoisted_17, _toDisplayString(memoState.value), 1),
          _createElementVNode("button", {
            type: "button",
            class: "cal-memo-save",
            disabled: saving.value,
            onClick: saveMemo
          }, "저장", 8, _hoisted_18)
        ])
      ])
    ])
  ]))
}
}

})